import { useEffect, useRef, useState } from "react";
import { Badge, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import Moment from "moment";
import StorageService from '../../services/api/Storage/index.js';
import { fileSizeSI, uuidv4 } from "../../settings/shared/commonMethods.js";
import { API_RESPONSE } from "../../settings/shared/constants.js";
import toast from "react-hot-toast";

const STORAGE_FILES_VIEW_CSS = `
  .sfv-root {
    --sfv-surface: #ffffff;
    --sfv-bg: #fafaf9;
    --sfv-border: rgba(0,0,0,0.07);
    --sfv-border-md: rgba(0,0,0,0.12);
    --sfv-text: #111110;
    --sfv-muted: #6b6b68;
    --sfv-subtle: #a1a19d;
    --sfv-blue: #2563eb;
    --sfv-blue-bg: rgba(37,99,235,0.07);
    --sfv-hover: rgba(0,0,0,0.025);
    font-family: 'DM Sans', system-ui, sans-serif;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    background: var(--sfv-surface);
    overflow: hidden;
  }

  .sfv-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--sfv-border);
    background: var(--sfv-surface);
    flex-shrink: 0;
  }

  .sfv-search {
    position: relative;
    flex: 1;
    min-width: 200px;
    max-width: 420px;
  }

  .sfv-search input {
    width: 100%;
    height: 36px;
    border-radius: 10px;
    border: 1px solid var(--sfv-border);
    padding: 0 34px 0 12px;
    font-size: 12.5px;
    color: var(--sfv-text);
    background: #fff;
    outline: none;
    transition: border-color 0.12s, box-shadow 0.12s;
  }

  .sfv-search input:focus {
    border-color: var(--sfv-blue);
    box-shadow: 0 0 0 3px rgba(37,99,235,0.12);
  }

  .sfv-search-ico {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--sfv-subtle);
    font-size: 12px;
    cursor: pointer;
  }

  .sfv-tools {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .sfv-tool-btn {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    border: 1px solid var(--sfv-border);
    background: var(--sfv-bg);
    color: var(--sfv-muted);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s, color 0.12s;
  }

  .sfv-tool-btn:hover {
    background: #fff;
    border-color: var(--sfv-border-md);
    color: var(--sfv-text);
  }

  .sfv-head {
    display: grid;
    grid-template-columns: 1.8fr 0.45fr 1fr;
    gap: 8px;
    padding: 0 14px;
    height: 34px;
    align-items: center;
    border-bottom: 1px solid var(--sfv-border);
    background: var(--sfv-bg);
    flex-shrink: 0;
  }

  .sfv-head-cell {
    font-size: 11px;
    font-weight: 700;
    color: var(--sfv-muted);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    cursor: pointer;
    user-select: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .sfv-scroll {
    flex: 1;
    overflow: auto;
    position: relative;
  }

  .sfv-loading {
    position: absolute;
    inset: 0;
    background: rgba(255,255,255,0.72);
    backdrop-filter: blur(1px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    z-index: 5;
  }

  .sfv-spinner {
    width: 26px;
    height: 26px;
    border-radius: 999px;
    border: 2px solid rgba(17,17,16,0.12);
    border-top-color: rgba(37,99,235,0.85);
    animation: sfvSpin 0.9s linear infinite;
  }

  @keyframes sfvSpin { to { transform: rotate(360deg); } }

  .sfv-loading-text {
    font-size: 12px;
    color: var(--sfv-muted);
    font-weight: 600;
    letter-spacing: 0.02em;
  }

  .sfv-row {
    display: grid;
    grid-template-columns: 1.8fr 0.45fr 1fr;
    gap: 8px;
    padding: 0 14px;
    height: 44px;
    align-items: center;
    border-bottom: 1px solid rgba(0,0,0,0.05);
    background: #fff;
    transition: background 0.12s;
  }

  .sfv-row:hover {
    background: var(--sfv-hover);
  }

  .sfv-cell-name {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .sfv-check {
    color: var(--sfv-muted);
    cursor: pointer;
    font-size: 14px;
    width: 18px;
    text-align: center;
    flex-shrink: 0;
  }

  .sfv-ico {
    width: 22px;
    height: 22px;
    border-radius: 8px;
    background: var(--sfv-bg);
    border: 1px solid var(--sfv-border);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--sfv-muted);
    font-size: 12px;
  }

  .sfv-ico.folder { color: #d97706; border-color: rgba(217,119,6,0.18); background: rgba(217,119,6,0.06); }

  .sfv-name {
    font-size: 12.5px;
    font-weight: 600;
    color: var(--sfv-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sfv-name.folder {
    cursor: pointer;
  }

  .sfv-name.folder:hover {
    color: var(--sfv-blue);
    text-decoration: underline;
  }

  .sfv-size,
  .sfv-date {
    font-size: 12px;
    color: var(--sfv-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sfv-date { text-align: right; }
  .sfv-size { text-align: left; }

  .sfv-pathbar {
    flex-shrink: 0;
    padding: 8px 14px;
    border-bottom: 1px solid var(--sfv-border);
    background: var(--sfv-surface);
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
  }

  .sfv-crumb {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--sfv-muted);
    border: 1px solid transparent;
    cursor: pointer;
    background: transparent;
  }

  .sfv-crumb:hover {
    background: var(--sfv-blue-bg);
    color: var(--sfv-text);
    border-color: var(--sfv-border);
  }

  .sfv-crumb.active {
    cursor: default;
    color: var(--sfv-blue);
    background: rgba(37,99,235,0.06);
    border-color: rgba(37,99,235,0.15);
  }

  .sfv-crumb-sep { color: var(--sfv-subtle); font-size: 11px; user-select: none; padding: 0 2px; }

  .sfv-footer {
    flex-shrink: 0;
    padding: 10px 14px;
    border-top: 1px solid var(--sfv-border);
    background: #fff;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  .sfv-footer .btn {
    border-radius: 10px;
    font-size: 12.5px;
    font-weight: 600;
    padding: 7px 10px;
  }
`;


const StorageFilesView = (props) => {
    var searchTimeOut = 0;
    const [searchWord, setSearchWord] = useState("")
    const [getCurrentFolder, setCurrentFolder] = useState(null)
    const [getPrefix, setPrefix] = useState("")
    const [getstorageFiles, setstorageFiles] = useState([])
    const [getstorageFolders, setstorageFolders] = useState([])
    const [storageFilesToBeAdded, setStorageFilesToBeAdded] = useState(props.selectedData?.files || []);
    const [storageFoldersToBeAdded, setStorageFoldersToBeAdded] = useState(props.selectedData?.folders || []);
    const [getnextPageToken, setnextPageToken] = useState(null)
    const [showUploadModel, setShowUploadModel] = useState(false)
    const [loading, setLoading] = useState(false)
    const [getSortType, setSortType] = useState("desc")
    const activeReqIdRef = useRef(0);

    useEffect(() => {
        const id = "storage-files-view-styles-v2";
        if (!document.getElementById(id)) {
            const tag = document.createElement("style");
            tag.id = id;
            tag.textContent = STORAGE_FILES_VIEW_CSS;
            document.head.appendChild(tag);
        }
    }, []);

    useEffect(() => {
        setstorageFiles([])
        setstorageFolders([])
        setPrefix("")
        setSearchWord("")
        fetchStorageFilesFolders(getPrefix, null, [], []);
    }, [props.storageId, props.rootFolder])

    const fetchStorageFilesFolders = (prefix, pageToken, files, folders) => {
        const reqId = ++activeReqIdRef.current;
        setLoading(true);
        return StorageService.listObjects(prefix, props.storageId, props.rootFolder, pageToken)
            .then((response) => {
                if (reqId !== activeReqIdRef.current) return;
                if (response?.status !== API_RESPONSE.FAILED) {
                    let foldersSort = [...(response.prefixes || [])].sort((a, b) => {
                        a = typeof a === 'string' ? a.toLowerCase() : a.toString();
                        b = typeof b === 'string' ? b.toLowerCase() : b.toString();
                        return b.localeCompare(a);
                    });
                    let filesSort = [...(response.result || [])].sort((a, b) => {
                        let _a = a?.name;
                        let _b = b?.name;
                        _a = typeof _a === 'string' ? _a.toLowerCase() : (_a ?? "").toString();
                        _b = typeof _b === 'string' ? _b.toLowerCase() : (_b ?? "").toString();
                        return _b.localeCompare(_a);
                    });
                    files = [...files, ...filesSort];
                    folders = [...folders, ...foldersSort];

                    setnextPageToken(response.next_page_token);
                    setstorageFiles(files);
                    setstorageFolders(folders);
                    setCurrentFolder(prefix);
                } else {
                    setstorageFiles([]);
                    setstorageFolders([]);
                    toast.error(response?.errorMessage);
                }
            })
            .catch((err) => {
                if (reqId !== activeReqIdRef.current) return;
                setstorageFiles([]);
                setstorageFolders([]);
                toast.error(err?.message || "Failed to load storage objects");
            })
            .finally(() => {
                if (reqId !== activeReqIdRef.current) return;
                setLoading(false);
            });
    }


    const onNavigationClick = (prefix) => {
        fetchStorageFilesFolders(prefix || "", null, [], []);
    };

    const onSearch = (value) => {
        if (typeof value === "object") {
            searchWithTimeout(value.target.value)
            return true
        }
        searchWithTimeout(value)
    }

    const searchWithTimeout = (value) => {
        setSearchWord(value)
        if (searchTimeOut) clearTimeout(searchTimeOut);
        searchTimeOut = setTimeout(() => {
            searchWithPrefixes(value);
        }, 1000);
    }

    const searchWithPrefixes = (value) => {
        if (value === "") {
            onRefresh();
            return false
        }
        const reqId = ++activeReqIdRef.current;
        setLoading(true);
        StorageService.listObjectFilter(getCurrentFolder, value, props.storageId,
            props.rootFolder)
            .then((response) => {
                if (reqId !== activeReqIdRef.current) return;
                setstorageFiles(response.result);
                setstorageFolders(response.prefixes);
                setnextPageToken(response.next_page_token);
            }).catch((error) => {
                if (reqId !== activeReqIdRef.current) return;
                toast.error(error?.message || "Search failed");
            }).finally(() => {
                if (reqId !== activeReqIdRef.current) return;
                setLoading(false);
            });
    }

    const clickSorting = (type, sortType) => {
        // Sort folders in ascending order
        let foldersSort = [...getstorageFolders].sort((a, b) => {
            // Convert to strings and force lowercase for comparison
            a = typeof a === 'string' ? a.toLowerCase() : a.toString();
            b = typeof b === 'string' ? b.toLowerCase() : b.toString();

            let data = type === "asc" ? a.localeCompare(b) : b.localeCompare(a)
            return data
        });
        // Sort files in ascending order by 'name' property
        let filesSort = [...getstorageFiles].sort((a, b) => {
            let _a = typeof a[sortType] === 'string' ? a[sortType].toLowerCase() : a[sortType].toString();
            let _b = typeof b[sortType] === 'string' ? b[sortType].toLowerCase() : b[sortType].toString();
            if (sortType === "name") {
                let data = type === "asc" ? _a.localeCompare(_b) : _b.localeCompare(_a)
                return data
            }
            if (sortType === "size") {
                let data = type === "asc" ? _a - _b : _b - _a
                return data
            }
            if (sortType === "created") {
                // Ensure the createdDate values are strings for comparison
                if (type === "asc") {
                    return _a < _b ? -1 : (_a > _b ? 1 : 0);
                } else {
                    return _b < _a ? -1 : (_b > _a ? 1 : 0);
                }
            }
        });
        setstorageFiles(filesSort);
        setstorageFolders(foldersSort);
    };

    const onRefresh = () => {
        setstorageFiles([])
        setstorageFolders([])
        fetchStorageFilesFolders(getCurrentFolder, null, [], []);
    }

    const toggleUpload = () => {
        if (showUploadModel) {
            setShowUploadModel(false);
        } else {
            setShowUploadModel(true);
        }
    }

    const onFolderClick = event => {
        event.stopPropagation();
        event.preventDefault();
        setstorageFiles([])
        setstorageFolders([])
        setnextPageToken(null)
        fetchStorageFilesFolders(event.target.dataset.name, null, [], [])
    }

    const onFolderNavigate = (folderPath) => {
        setstorageFiles([]);
        setstorageFolders([]);
        setnextPageToken(null);
        fetchStorageFilesFolders(folderPath, null, [], []);
    };

    const updateStorageFolders = (storageFolder, addFlag) => {
        if (addFlag) {
            setStorageFoldersToBeAdded(prevFolders => [...prevFolders, storageFolder]);
        } else {
            const updatedFolders = storageFoldersToBeAdded.filter(
                item =>
                    !(item.filePath === storageFolder.filePath && item.storageId === storageFolder.storageId)
            );
            setStorageFoldersToBeAdded(updatedFolders)
        }
    }

    const updateStorageFiles = (storageFile, addFlag) => {
        if (addFlag) {
            setStorageFilesToBeAdded(prevFiles => [...prevFiles, storageFile]);
        } else {
            const updatedFiles = storageFilesToBeAdded.filter(
                item =>
                    !(item.filePath === storageFile.filePath && item.storageId === storageFile.storageId)
            );
            setStorageFilesToBeAdded(updatedFiles)
        }
    }

    const fileCheckClicked = (event, isDir) => {
        event.preventDefault()
        event.stopPropagation()
        let updateFlag = null
        if (event.target.dataset.checked === "0") {
            event.target.className = "fas fa-check-square pr-2"
            event.target.dataset.checked = "1"
            updateFlag = true
        } else {
            event.target.className = "far fa-square pr-2"
            event.target.dataset.checked = "0"
            updateFlag = false
        }
        let selectedFile = {
            storageId: props.storageId,
            rootFolder: props.rootFolder,
            filePath: event.target.dataset.name,
            envName: "",
            dir: isDir,
            cloudType: props.cloudType
        }
        event.target.dataset.type === "folder" ? updateStorageFolders(selectedFile, updateFlag) :
            updateStorageFiles(selectedFile, updateFlag)
    }

    const trimFolderName = folderName => {
        if (folderName.endsWith('/')) {
            folderName = folderName.slice(0, -1);
        }
        return folderName.split('/').pop()
    };

    const trimFileName = (fileName) => {
        return fileName?.split('/').pop()
    };

    const findIcon = contentType => {
        if (!contentType) {
            return 'fas fa-file'
        } else if (contentType.startsWith('audio/')) {
            return 'fas fa-file-audio'
        } else if (contentType.startsWith('image/')) {
            return 'fas fa-file-image'
        } else if (contentType.startsWith('text/')) {
            return 'fas fa-file-alt'
        } else {
            return 'fas fa-file-alt'
        }
    };

    const saveSelectedFiles = () => {
        if (props.selectFromLocation === "operation") {
            props._getStorageOperationInfo(getCurrentFolder)
            return true;
        }
        if (storageFilesToBeAdded.length > 0 || storageFoldersToBeAdded.length > 0) {
            let savedFiles = {
                folders: storageFoldersToBeAdded,
                files: storageFilesToBeAdded
            }
            props._getSelectedFiles(savedFiles)
            return true;
        }
    }

    const handleScroll = (e) => {
        if (loading || !getnextPageToken) return;
        const el = e.currentTarget;
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        const nearBottom = remaining <= 24; // px threshold
        if (nearBottom) {
            fetchStorageFilesFolders(getCurrentFolder, getnextPageToken, getstorageFiles, getstorageFolders);
        }
    }

    const buildCrumbs = () => {
        const current = getCurrentFolder || "";
        const parts = current ? current.split("/").filter(Boolean) : [];
        const crumbs = [];

        const pushSep = (key) => crumbs.push(<span key={key} className="sfv-crumb-sep" aria-hidden="true">/</span>);

        crumbs.push(
            <button
                key="home"
                type="button"
                className={`sfv-crumb${parts.length === 0 ? " active" : ""}`}
                onClick={parts.length === 0 ? undefined : () => onNavigationClick("")}
            >
                <i className="fas fa-home" style={{ fontSize: 11 }} /> Home
            </button>
        );

        let acc = "";
        parts.forEach((p, idx) => {
            acc += `${p}/`;
            pushSep(`sep-${idx}`);
            const isLast = idx === parts.length - 1;
            crumbs.push(
                <button
                    key={acc}
                    type="button"
                    className={`sfv-crumb${isLast ? " active" : ""}`}
                    onClick={isLast ? undefined : () => onNavigationClick(acc)}
                >
                    {p}
                </button>
            );
        });
        return crumbs;
    };

    return (
        <>
            <div className="sfv-root">
                <div className="sfv-toolbar">
                    <div className="sfv-search">
                        <input
                            type="search"
                            value={searchWord}
                            placeholder="Search files and folders…"
                            onChange={onSearch}
                            aria-label="Search files and folders"
                        />
                        <span className="sfv-search-ico" onClick={onSearch} aria-hidden="true">
                            <i className="fa-solid fa-magnifying-glass" />
                        </span>
                    </div>
                    <div className="sfv-tools">
                    <OverlayTrigger placement="top"
                        overlay={<Tooltip id="tooltip">
                            <span className="df-text-black-50">Upload</span>
                        </Tooltip>}>
                        <button type="button" className="sfv-tool-btn" onClick={() => toggleUpload()} aria-label="Upload">
                            <i className="fa-solid fa-cloud-arrow-up" />
                        </button>
                    </OverlayTrigger>
                    <OverlayTrigger placement="top"
                        overlay={<Tooltip id="tooltip">
                            <span className="df-text-black-50">Refresh</span>
                        </Tooltip>}>
                        <button type="button" className="sfv-tool-btn" onClick={() => onRefresh()} aria-label="Refresh">
                            <i className="fa-solid fa-repeat" />
                        </button>
                    </OverlayTrigger>
                    </div>
                </div>

                <div className="sfv-pathbar" aria-label="Current folder path">
                    {buildCrumbs()}
                </div>

                {(getstorageFolders.length > 0 || getstorageFiles.length > 0) && (
                    <div className="sfv-head" role="row" aria-label="List header">
                        <span
                            className="sfv-head-cell"
                            onClick={() => { const t = getSortType === "desc" ? "asc" : "desc"; setSortType(t); clickSorting(t, "name"); }}
                        >
                            Name <i className="fa-solid fa-caret-up" />
                        </span>
                        <span
                            className="sfv-head-cell"
                            onClick={() => { const t = getSortType === "desc" ? "asc" : "desc"; setSortType(t); clickSorting(t, "size"); }}
                        >
                            Size <i className="fa-solid fa-caret-up" />
                        </span>
                        <span
                            className="sfv-head-cell"
                            style={{ justifyContent: "flex-end" }}
                            onClick={() => { const t = getSortType === "desc" ? "asc" : "desc"; setSortType(t); clickSorting(t, "created"); }}
                        >
                            Modified <i className="fa-solid fa-caret-up" />
                        </span>
                    </div>
                )}

                <div className="sfv-scroll" onScroll={handleScroll}>
                    {loading && (
                        <div className="sfv-loading" role="status" aria-live="polite" aria-label="Loading">
                            <div className="sfv-spinner" aria-hidden="true" />
                            <div className="sfv-loading-text">Loading…</div>
                        </div>
                    )}

                    {!loading && getstorageFolders.length + getstorageFiles.length === 0 && (
                        <div style={{ padding: 24, textAlign: "center", color: "#9ca3af", fontSize: 12 }}>
                            No files or folders
                        </div>
                    )}

                    {getstorageFolders.map((item, index) => {
                        const folderSelected = storageFoldersToBeAdded.some(
                            (f) => (f.storageId === props.storageId || f.storageId === null) && f.filePath === item
                        );
                        return (
                            <div key={item || index} className="sfv-row" role="row">
                                <div className="sfv-cell-name">
                                    {props.showSelectBox && (
                                        <i
                                            className={`sfv-check ${folderSelected ? "fas fa-check-square" : "far fa-square"}`}
                                            onClick={(e) =>
                                                fileCheckClicked(
                                                    {
                                                        preventDefault: () => {},
                                                        stopPropagation: () => {},
                                                        target: {
                                                            dataset: { type: "folder", name: item, full_path: item, checked: folderSelected ? "1" : "0" },
                                                            className: "",
                                                        },
                                                    },
                                                    true
                                                )
                                            }
                                            role="button"
                                            tabIndex={0}
                                            aria-label={folderSelected ? "Unselect folder" : "Select folder"}
                                        />
                                    )}
                                    <span className="sfv-ico folder" aria-hidden="true">
                                        <i className="fas fa-folder" />
                                    </span>
                                    <span
                                        className="sfv-name folder"
                                        title={item}
                                        onClick={() => onFolderNavigate(item)}
                                    >
                                        {trimFolderName(item)}
                                    </span>
                                </div>
                                <span className="sfv-size">—</span>
                                <span className="sfv-date">—</span>
                            </div>
                        );
                    })}

                    {getstorageFiles.map((file, index) => {
                        const fileSelected = storageFilesToBeAdded.some(
                            (f) => (f.storageId === props.storageId || f.storageId === null) && f.filePath === file.name
                        );
                        return (
                            <div key={file.name || index} className="sfv-row" role="row">
                                <div className="sfv-cell-name">
                                    {props.showSelectBox && (
                                        <i
                                            className={`sfv-check ${fileSelected ? "fas fa-check-square" : "far fa-square"}`}
                                            onClick={(e) =>
                                                fileCheckClicked(
                                                    {
                                                        preventDefault: () => {},
                                                        stopPropagation: () => {},
                                                        target: {
                                                            dataset: { type: "file", name: file.name, full_path: file.full_path, checked: fileSelected ? "1" : "0" },
                                                            className: "",
                                                        },
                                                    },
                                                    false
                                                )
                                            }
                                            role="button"
                                            tabIndex={0}
                                            aria-label={fileSelected ? "Unselect file" : "Select file"}
                                        />
                                    )}
                                    <span className="sfv-ico" aria-hidden="true">
                                        <i className={findIcon(file?.content_type)} />
                                    </span>
                                    <span className="sfv-name" title={file.name}>
                                        {trimFileName(file?.name)}
                                    </span>
                                </div>
                                <span className="sfv-size">{fileSizeSI(file.size)}</span>
                                <span className="sfv-date" title={Moment(file.created).format('MMM DD, YYYY hh:mm A')}>
                                    {Moment(file.created).format('MMM DD, YYYY')}
                                </span>
                            </div>
                        );
                    })}

                    {getnextPageToken === null && !loading && (getstorageFolders.length + getstorageFiles.length > 0) && (
                        <div style={{ textAlign: "center", padding: 12, color: "#a1a19d", fontSize: 11.5, letterSpacing: "0.04em" }}>
                            — End of list —
                        </div>
                    )}
                </div>

            {/* Cancel & Save */}
            {props.actionFooter !== null && props.actionFooter !== undefined && props.actionFooter && (
                <div className="sfv-footer">
                    <Button variant="light" size="sm" onClick={() => props._getSelectedFiles(null)}>
                        <span className="btn-label">
                            <i className="fas fa-times-circle text-danger"></i>
                        </span>
                        &nbsp;&nbsp;Cancel
                    </Button>
                    <Button variant="primary" size="sm" onClick={saveSelectedFiles}>
                        <span className="btn-label">
                            <i className="fas fa-save mr-1"></i>
                        </span>
                        &nbsp;&nbsp;{props.buttonText}
                    </Button>
                </div>
            )}
            </div>
        </>
    )

}

export default StorageFilesView;